import { activeSidebarChatIdAtom, sidebarChatIdAtomFamily } from '@/atoms/chatSidebarAtoms';
import { ChatMode } from '@/components/chat/types/types';
import { IS_AUTO_MODE_ONLY_ENABLED } from '@/features/flags/legacy-flags';
import {
  filterChatsForWorkAvailability,
  getChatHref,
  isWorkHarnessChat,
} from '@heidi/agents/contracts';
import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { toast, ToastActionButton } from '@heidi/design-system/toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import {
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { Loader } from '@heidi/snowflake-ui/loader';
import { useMutateDeleteChat } from '@/hooks/mutation/useMutateDeleteChat';
import {
  GET_SESSION_CHATS,
  invalidateSessionChats,
  useQuerySessionChats,
} from '@heidi/session/data';
import { cn } from '@/lib/cn';
import type { ChatListResponse } from '@heidi/session/contracts';
import type { ChatListItem } from '@/types';
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { ChevronDown, Clock, Trash2 } from 'lucide-react';
import { m } from 'motion/react';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

const UNDO_DELAY_MS = 5000;
const EXIT_ANIMATION_MS = 350;

/**
 * Untranslated backend placeholder title for a chat with no name yet; must
 * match backend copy (see `deriveOptimisticChatTitle` in
 * `src/hooks/mutation/useMutateCreateChat.ts`, the source of truth). Trimmed,
 * case-insensitive so this stays aligned even if casing/whitespace varies.
 *
 * Duplicated from `src/utils/chatPlaceholderTitle.ts` rather than imported:
 * a Chat-quarantine file importing a new `src/utils/**` module grows the
 * chat-baseline K2 ratchet (outbound pairs into that zone), which is
 * shrink-only — see `scripts/chat-baseline/README.md`.
 */
const isBackendPlaceholderChatTitle = (title: string | null | undefined): boolean => {
  const trimmed = title?.trim();
  return !trimmed || trimmed.toLowerCase() === 'new chat';
};

interface SessionChatHistoryDropdownProps {
  sessionId: string;
  currentChatId?: string | null;
  mode: ChatMode;
  onSelectChat?: (chatId: string) => void;
  /** Chat title from parent (passed from useChatBackend via chatMeta) */
  chatTitle?: string;
  /** Compact icon-only trigger for use in floating chip row */
  iconOnly?: boolean;
  /**
   * Resolved Projects/Work gate. Hosts that already read flags pass this so
   * Chat does not import `@/features/flags`.
   */
  isWorkEnabled: boolean;
}

export const SessionChatHistoryDropdown = ({
  sessionId,
  currentChatId,
  mode,
  onSelectChat,
  chatTitle,
  iconOnly = false,
  isWorkEnabled,
}: SessionChatHistoryDropdownProps) => {
  const intl = useIntl();
  const router = useRouter();
  const queryClient = useQueryClient();
  const setSidebarChatId = useSetAtom(sidebarChatIdAtomFamily(sessionId));
  const setActiveSidebarChatId = useSetAtom(activeSidebarChatIdAtom);
  const { data: chatsData, isLoading } = useQuerySessionChats(sessionId);
  const { mutate: deleteChatMutation } = useMutateDeleteChat();
  const undoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const defaultChatTitleLabel = intl.formatMessage({
    defaultMessage: 'New chat',
    id: 'ex6NWuIWQJ',
    description: 'Placeholder title shown for a chat that has not been named yet',
  });
  const askHeidiModeLabel = intl.formatMessage({
    defaultMessage: 'Ask Heidi', id: 'XTqPPZforx',
    description: 'Chat header: legacy Ask Heidi mode label',
  });
  const autoModeLabel = intl.formatMessage({
    defaultMessage: 'Auto', id: 'qW9FYjLZmf',
    description: 'Chat header: Auto mode label',
  });
  const evidenceModeLabel = intl.formatMessage({
    defaultMessage: 'Evidence', id: 'hGScwynDxX',
    description: 'Chat header: Evidence mode label',
  });
  const chatDeletedToastLabel = intl.formatMessage({
    defaultMessage: 'Chat deleted', id: 'o7vMGJ8Y2C',
    description: 'Toast title after deleting a chat from the session history dropdown',
  });
  const undoLabel = intl.formatMessage({
    defaultMessage: 'Undo', id: 'PD0ds0qaRp',
    description: 'Undo action on the chat-deleted toast',
  });
  const deleteChatLabel = intl.formatMessage({
    defaultMessage: 'Delete', id: 'fhn+D34OAW',
    description: 'Session history dropdown: delete a chat',
  });
  const chatHistoryLabel = intl.formatMessage({
    defaultMessage: 'Chat history', id: 'JwKD0sPgZk',
    description: 'Session history dropdown trigger label',
  });
  const sessionHistoryEmptyLabel = intl.formatMessage({
    defaultMessage: 'No chats for this session yet', id: 'S4DBwHwThF',
    description: 'Session history dropdown: empty state',
  });

  const headerTitle = IS_AUTO_MODE_ONLY_ENABLED()
    ? chatTitle && !isBackendPlaceholderChatTitle(chatTitle)
      ? chatTitle
      : defaultChatTitleLabel
    : mode === ChatMode.LEGACY
      ? askHeidiModeLabel
      : mode === ChatMode.AUTO
        ? autoModeLabel
        : evidenceModeLabel;

  const currentChat = useMemo(() => {
    if (!currentChatId || !chatsData?.chats) return null;
    return chatsData.chats.find((chat) => chat.id === currentChatId);
  }, [currentChatId, chatsData?.chats]);

  const visibleChats = useMemo(() => {
    const chats = chatsData?.chats ?? [];
    return filterChatsForWorkAvailability(chats, isWorkEnabled);
  }, [chatsData?.chats, isWorkEnabled]);

  const hasChats = visibleChats.length > 0;

  const sessionChatsQueryKey = useMemo(() => [GET_SESSION_CHATS, sessionId, 1, 20], [sessionId]);

  const handleSelectChat = useCallback(
    (chat: ChatListItem) => {
      if (isWorkHarnessChat(chat)) {
        void router.push(getChatHref(chat));
        return;
      }

      setActiveSidebarChatId(null);

      if (onSelectChat) {
        onSelectChat(chat.id);
      } else {
        setSidebarChatId(chat.id);
      }
    },
    [onSelectChat, router, setActiveSidebarChatId, setSidebarChatId]
  );

  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDeleteChat = useCallback(
    (e: React.MouseEvent, chat: ChatListItem) => {
      e.stopPropagation();
      e.preventDefault();

      if (deletingChatId) return;

      const previousSessionChats =
        queryClient.getQueryData<ChatListResponse>(sessionChatsQueryKey);

      // Phase 1: mark item as deleting (triggers visual animation)
      setDeletingChatId(chat.id);

      // Phase 2: after animation completes, remove from cache + show toast
      exitTimerRef.current = setTimeout(() => {
        queryClient.setQueryData<ChatListResponse>(sessionChatsQueryKey, (old) =>
          old ? { ...old, chats: old.chats.filter((c) => c.id !== chat.id) } : old
        );
        setDeletingChatId(null);

        if (currentChatId === chat.id) {
          const remaining = (previousSessionChats?.chats ?? []).filter(
            (c) => c.id !== chat.id
          );
          const visibleRemaining = filterChatsForWorkAvailability(
            remaining,
            isWorkEnabled
          );
          // Stay in Evidence: a v2 remainder would router.push out of the session.
          const nextChat = visibleRemaining.find((c) => !isWorkHarnessChat(c));
          if (nextChat) {
            handleSelectChat(nextChat);
          } else {
            setActiveSidebarChatId(null);
            setSidebarChatId(null);
          }
        }

        undoTimeoutRef.current = setTimeout(() => {
          deleteChatMutation(chat.id, {
            onSettled: () => void invalidateSessionChats(sessionId),
          });
        }, UNDO_DELAY_MS);

        toast({
          title: chatDeletedToastLabel,
          action: (
            <ToastActionButton
              onClick={() => {
                if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);
                queryClient.setQueryData(sessionChatsQueryKey, previousSessionChats);
                void invalidateSessionChats(sessionId);
              }}
            >
              {undoLabel}
            </ToastActionButton>
          ),
        });
      }, EXIT_ANIMATION_MS);
    },
    [
      deletingChatId,
      queryClient,
      sessionChatsQueryKey,
      currentChatId,
      handleSelectChat,
      isWorkEnabled,
      setActiveSidebarChatId,
      setSidebarChatId,
      deleteChatMutation,
      sessionId,
      chatDeletedToastLabel,
      undoLabel,
    ]
  );

  const renderChatItem = (chat: ChatListItem) => {
    const isSelected = chat.id === currentChatId;

    return (
      <DropdownMenuItem
        key={chat.id}
        onClick={() => handleSelectChat(chat)}
        className={cn(
          'group/chat-item flex items-center gap-2 py-2',
          isSelected && 'bg-background-tertiary'
        )}
        data-testid={`session-chat-item-${chat.id}`}
      >
        <div className="flex min-w-0 flex-1 items-center">
          <TypographyP2 className={cn('truncate', isSelected && 'font-medium')}>
            {isBackendPlaceholderChatTitle(chat.title) ? defaultChatTitleLabel : chat.title}
          </TypographyP2>
        </div>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="destructive_ghost"
                size="xxs"
                iconOnly
                aria-label={deleteChatLabel}
                className={cn(
                  'shrink-0 opacity-0 transition-opacity',
                  'focus-visible:opacity-100 group-hover/chat-item:opacity-100'
                )}
                onClick={(e) => handleDeleteChat(e, chat)}
              >
                <Trash2 />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              {deleteChatLabel}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </DropdownMenuItem>
    );
  };

  const trigger = iconOnly ? (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="xs"
              iconOnly
              className="size-7 text-text-tertiary hover:text-text-secondary"
              aria-label={chatHistoryLabel}
              data-testid="session-chat-history-dropdown"
            >
              <Clock size={14} />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">{chatHistoryLabel}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size="sm"
        className="gap-1.5 px-2"
        data-testid="session-chat-history-dropdown"
      >
        <TypographyP2Bold>
          {currentChat
            ? !isBackendPlaceholderChatTitle(currentChat.title)
              ? currentChat.title
              : defaultChatTitleLabel
            : headerTitle}
        </TypographyP2Bold>
        <ChevronDown size={14} className="text-text-secondary" />
      </Button>
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu>
      {trigger}
      <DropdownMenuContent align="start" className="max-h-80 w-72 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader size={16} />
          </div>
        )}

        {!isLoading && !hasChats && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ opacity: { duration: 0.15, delay: 0.1 } }}
            className="px-2 py-4 text-center"
          >
            <TypographyP2 className="text-text-secondary">
              {sessionHistoryEmptyLabel}
            </TypographyP2>
          </m.div>
        )}

        {!isLoading &&
          hasChats &&
          visibleChats.map((chat) => {
            const isDeleting = deletingChatId === chat.id;
            return (
              <m.div
                key={chat.id}
                layout
                initial={false}
                animate={
                  isDeleting
                    ? {
                        opacity: 0,
                        x: -12,
                        height: 0,
                        marginTop: 0,
                        marginBottom: 0,
                      }
                    : { opacity: 1, x: 0, height: 'auto' }
                }
                style={isDeleting ? { overflow: 'hidden' } : undefined}
                transition={{
                  opacity: { duration: 0.2, ease: 'easeOut' },
                  x: { duration: 0.25, ease: 'easeOut' },
                  height: { duration: 0.25, ease: 'easeOut', delay: 0.1 },
                  layout: { duration: 0.25, ease: [0.25, 0.1, 0.25, 1] },
                }}
              >
                {renderChatItem(chat)}
              </m.div>
            );
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
